import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { AvailableLocale } from '@models/Lang'
import { localFlags } from '@utils/featureFlags'
import { langList } from '../factories/Utils'

interface LocalizableContextProps {
  langCode: AvailableLocale
  setLangCode: (langCode: AvailableLocale) => void
  registerSelector: () => () => void
  registerField: () => () => void
  hasLocalizableFields: boolean
  headerSelector: boolean
}

const LocalizableContext = createContext<LocalizableContextProps | null>(null)

export const LocalizableProvider: React.FC<{
  children: React.ReactNode
  /**
   * The surface mounting this provider carries the language selector itself
   * (the SBRM drawer/modal header, the push wizard header). Fields below then
   * never render their own selector, so no form has to opt out field by field.
   */
  headerSelector?: boolean
}> = ({ children, headerSelector = false }) => {
  const [langCode, setLangCode] = useState<AvailableLocale>(langList[0].code)

  // Ref-counts of the mounted selectors and localizable fields, so a consumer
  // (e.g. the header selector) can tell what the current subtree actually holds.
  const [selectorCount, setSelectorCount] = useState<number>(0)
  const [fieldCount, setFieldCount] = useState<number>(0)

  const registerSelector = useCallback(() => {
    setSelectorCount((count) => count + 1)
    return () => setSelectorCount((count) => count - 1)
  }, [])

  const registerField = useCallback(() => {
    setFieldCount((count) => count + 1)
    return () => setFieldCount((count) => count - 1)
  }, [])

  const hasSelector = selectorCount > 0
  const hasLocalizableFields = fieldCount > 0

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        localFlags.localizableWarning &&
        hasLocalizableFields &&
        !hasSelector
      ) {
        console.warn(
          '[Localizable] Warning: No LangSelector was found within this Localizable wrapper.'
        )
      }
    }, 1000)
    return () => clearTimeout(timeout)
  }, [hasSelector, hasLocalizableFields])

  const value = useMemo(
    () => ({
      langCode,
      setLangCode,
      registerSelector,
      registerField,
      hasLocalizableFields,
      headerSelector,
    }),
    [
      langCode,
      registerSelector,
      registerField,
      hasLocalizableFields,
      headerSelector,
    ]
  )

  return (
    <LocalizableContext.Provider value={value}>
      {children}
    </LocalizableContext.Provider>
  )
}

export const useLocalizable = () => useContext(LocalizableContext) ?? null
